/**
 * Size Guide Drawer — unit toggle + Swiper + Rebuy/Shopify recos (NGH-619)
 */
class SizeGuideDrawerRoot extends HTMLElement {
  static MODAL_URL_PARAM = 'modals'
  static MODAL_URL_VALUE = 'size-guide-dialog'

  connectedCallback() {
    this.carousels = []
    this.onUnitToggle = this.onUnitToggle.bind(this)
    this.onTabClick = this.onTabClick.bind(this)
    this.onModalOpen = this.onModalOpen.bind(this)
    this.onRecosMutation = this.onRecosMutation.bind(this)
    this.onFitsToggle = this.onFitsToggle.bind(this)
    this.onFitsSummaryClick = this.onFitsSummaryClick.bind(this)
    this.onPopState = this.onPopState.bind(this)

    this.querySelectorAll('[data-size-guide-unit-toggle]').forEach((toggle) => {
      toggle.addEventListener('change', this.onUnitToggle)
    })

    this.querySelectorAll('.js-tab').forEach((tab) => {
      tab.addEventListener('click', this.onTabClick)
    })

    this.bindFitsInteractions()

    this.modal = this.closest('hh-modal')
    if (this.modal) {
      this.modalObserver = new MutationObserver(this.onModalOpen)
      this.modalObserver.observe(this.modal, {
        attributes: true,
        attributeFilter: ['open'],
      })
      window.addEventListener('popstate', this.onPopState)
      if (this.urlHasSizeGuideModal()) {
        this.setModalOpen(true)
      }
    }

    this.recosObserver = new MutationObserver(this.onRecosMutation)
    const recosHost = this.querySelector('product-recommendations') || this
    this.recosObserver.observe(recosHost, { childList: true, subtree: true })

    this.syncUnitsVisibility()
    this.relocateQuickViews()
    this.waitForSwiper().then(() => this.initCarousels())
  }

  disconnectedCallback() {
    this.querySelectorAll('[data-size-guide-unit-toggle]').forEach((toggle) => {
      toggle.removeEventListener('change', this.onUnitToggle)
    })
    this.querySelectorAll('.js-tab').forEach((tab) => {
      tab.removeEventListener('click', this.onTabClick)
    })
    this.unbindFitsInteractions()
    this.modalObserver?.disconnect()
    this.recosObserver?.disconnect()
    window.removeEventListener('popstate', this.onPopState)
    this.destroyCarousels()
  }

  urlHasSizeGuideModal() {
    const params = new URLSearchParams(window.location.search)
    return params.get(SizeGuideDrawerRoot.MODAL_URL_PARAM) === SizeGuideDrawerRoot.MODAL_URL_VALUE
  }

  syncModalUrl(isOpen) {
    const url = new URL(window.location.href)
    const hasParam = url.searchParams.get(SizeGuideDrawerRoot.MODAL_URL_PARAM) === SizeGuideDrawerRoot.MODAL_URL_VALUE

    if (isOpen && !hasParam) {
      url.searchParams.set(
        SizeGuideDrawerRoot.MODAL_URL_PARAM,
        SizeGuideDrawerRoot.MODAL_URL_VALUE
      )
      history.pushState({ ...history.state, sizeGuideDialog: true }, '', url)
      return
    }

    if (!isOpen && hasParam) {
      url.searchParams.delete(SizeGuideDrawerRoot.MODAL_URL_PARAM)
      history.pushState({ ...history.state, sizeGuideDialog: false }, '', url)
    }
  }

  setModalOpen(isOpen) {
    if (!this.modal) return
    if ('open' in this.modal) {
      this.modal.open = isOpen ? true : false
    } else {
      this.modal.setAttribute('open', isOpen ? 'true' : 'false')
    }
    document.body.style.overflowY = isOpen ? 'hidden' : ''
  }

  onPopState() {
    this.setModalOpen(this.urlHasSizeGuideModal())
  }

  bindFitsInteractions() {
    this.querySelectorAll('.size-guide-drawer__fits').forEach((details) => {
      details.addEventListener('toggle', this.onFitsToggle)
      const summary = details.querySelector('summary')
      summary?.addEventListener('click', this.onFitsSummaryClick)
    })
  }

  unbindFitsInteractions() {
    this.querySelectorAll('.size-guide-drawer__fits').forEach((details) => {
      details.removeEventListener('toggle', this.onFitsToggle)
      const summary = details.querySelector('summary')
      summary?.removeEventListener('click', this.onFitsSummaryClick)
    })
  }

  /**
   * Opening <details> focuses <summary>; browsers then scrollIntoView,
   * which horizontally jumps freeMode Swiper slides. Keep position fixed.
   */
  getCarouselPosition(target) {
    const carousel = target.closest('[data-size-guide-carousel]')
    const swiper = carousel?.swiper
    if (!swiper) return null
    if (swiper.params.cssMode) {
      return {
        swiper,
        cssMode: true,
        scrollLeft: swiper.wrapperEl?.scrollLeft ?? 0,
      }
    }
    return {
      swiper,
      cssMode: false,
      translate: swiper.getTranslate(),
    }
  }

  restoreCarouselPosition(state) {
    if (!state?.swiper) return
    const apply = () => {
      if (state.cssMode) {
        if (state.swiper.wrapperEl) {
          state.swiper.wrapperEl.scrollLeft = state.scrollLeft
        }
        return
      }
      state.swiper.setTranslate(state.translate)
      state.swiper.updateProgress()
      state.swiper.updateActiveIndex()
    }
    requestAnimationFrame(() => {
      apply()
      requestAnimationFrame(apply)
    })
    setTimeout(apply, 50)
  }

  onFitsSummaryClick(event) {
    const details = event.currentTarget.closest('details')
    if (!details) return

    // Prevent native focus scroll-into-view side effects on the carousel
    event.preventDefault()
    const state = this.getCarouselPosition(details)
    details.open = !details.open
    this.restoreCarouselPosition(state)
  }

  onFitsToggle(event) {
    const state = this.getCarouselPosition(event.currentTarget)
    this.restoreCarouselPosition(state)
  }

  onModalOpen() {
    const isOpen = this.modal?.getAttribute('open') === 'true'
    this.syncModalUrl(isOpen)
    if (!isOpen) return
    this.relocateQuickViews()
    requestAnimationFrame(() => {
      this.initCarousels()
      this.updateCarousels()
    })
  }

  onRecosMutation() {
    this.relocateQuickViews()
  }

  getQuickViewPortal() {
    const host = this.modal?.parentElement || document.body
    let portal = host.querySelector('[data-size-guide-qv-portal]')
    if (!portal) {
      portal = document.createElement('div')
      portal.className = 'size-guide-drawer__qv-portal'
      portal.setAttribute('data-size-guide-qv-portal', '')
      host.appendChild(portal)
    }
    return portal
  }

  /**
   * Quick views must sit outside the size guide modal so they remain
   * visible after the size guide closes (bag icon → QV behaviour).
   */
  relocateQuickViews() {
    const portal = this.getQuickViewPortal()
    this.querySelectorAll('hh-modal[data-size-guide-qv]').forEach((modal) => {
      if (modal.parentElement !== portal) {
        portal.appendChild(modal)
      }
    })
  }

  waitForSwiper(attempts = 40) {
    return new Promise((resolve) => {
      const tick = (left) => {
        if (typeof Swiper !== 'undefined' || left <= 0) {
          resolve()
          return
        }
        setTimeout(() => tick(left - 1), 50)
      }
      tick(attempts)
    })
  }

  getActiveTabKey() {
    const activePanel = this.querySelector('.js-tabpanel:not(.hidden)')
    return activePanel?.getAttribute('data-size-guide-tab') || null
  }

  syncUnitsVisibility() {
    const activeKey = this.getActiveTabKey()
    this.querySelectorAll('[data-size-guide-units-for]').forEach((units) => {
      const forTab = units.getAttribute('data-size-guide-units-for')
      const show = forTab === activeKey
      units.toggleAttribute('hidden', !show)
    })
  }

  onTabClick() {
    requestAnimationFrame(() => {
      this.syncUnitsVisibility()
      this.initCarousels()
      this.updateCarousels()
    })
  }

  onUnitToggle(event) {
    const targetKey = event.target.getAttribute('data-size-guide-tab-target')
    const tabPanel = targetKey
      ? this.querySelector(`[data-size-guide-tab="${targetKey}"]`)
      : event.target.closest('[data-size-guide-tab]')

    if (!tabPanel) return

    // checked = inches (default), unchecked = cm
    const unit = event.target.checked ? 'in' : 'cm'

    tabPanel.querySelectorAll('[data-size-guide-unit]').forEach((panel) => {
      const isActive = panel.getAttribute('data-size-guide-unit') === unit
      panel.classList.toggle('is-active', isActive)
      if (isActive) {
        panel.removeAttribute('hidden')
      } else {
        panel.setAttribute('hidden', '')
      }
    })

    requestAnimationFrame(() => {
      this.initCarousels()
      this.updateCarousels()
    })
  }

  isCarouselVisible(el) {
    return !el.closest('.hidden, [hidden]')
  }

  createSwiper(el) {
    if (el.swiper || typeof Swiper === 'undefined') return null

    const swiper = new Swiper(el, {
      slidesPerView: 'auto',
      spaceBetween: 3,
      freeMode: true,
      cssMode: true,
      autoHeight: true,
      watchOverflow: true,
      noSwiping: true,
      noSwipingClass: 'swiper-no-swiping',
      noSwipingSelector: '.size-guide-drawer__fits',
      navigation: {
        nextEl: el.querySelector('.swiper-button-next'),
        prevEl: el.querySelector('.swiper-button-prev'),
      },
    })

    this.carousels.push(swiper)
    return swiper
  }

  initCarousels() {
    if (typeof Swiper === 'undefined') return

    this.querySelectorAll('[data-size-guide-carousel]').forEach((el) => {
      if (el.swiper) return
      // Avoid init while hidden — wrong height leaves a large gap under Dimensions
      if (!this.isCarouselVisible(el)) return
      this.createSwiper(el)
    })
  }

  updateCarousels() {
    this.carousels.forEach((swiper) => {
      try {
        if (!this.isCarouselVisible(swiper.el)) return
        swiper.el.style.height = ''
        if (swiper.wrapperEl) swiper.wrapperEl.style.height = 'auto'
        swiper.update()
        swiper.el.style.height = 'auto'
        if (swiper.wrapperEl) swiper.wrapperEl.style.height = 'auto'
      } catch (e) {
        // ignore destroyed instances
      }
    })
  }

  destroyCarousels() {
    this.carousels.forEach((swiper) => {
      try {
        swiper.destroy(true, true)
      } catch (e) {
        // ignore
      }
    })
    this.carousels = []
  }
}

customElements.define('size-guide-drawer-root', SizeGuideDrawerRoot)
